import typeLabel from '@/constants/ActivityTypeLabelsStudents';

function createActivityTypeConf(activity) {
  const {
    type,
    data: { subType },
  } = activity;
  const initialTypeConf =
    ActivityTypesConf[type][subType] ?? ActivityTypesConf[type];
  console.log('[INITIAL]', initialTypeConf);
  const createSpecificConf = initialTypeConf.getConf;

  const createConf = createSpecificConf || createNormalConf;
  const activityTypeConf = createConf(
    activity.type,
    initialTypeConf,
    activity.data
  );
  console.log('[COMPLETE]', activityTypeConf);
  return activityTypeConf;
}

function createNormalConf(_, initialTypeConf) {
  const conf = {
    progress: initialTypeConf.progress,
    correctness: initialTypeConf.correctness,
    canLoadProgress: initialTypeConf.canLoadProgress,
    options: {
      repetitions: initialTypeConf.options.repetitions,
      allowedLevels: initialTypeConf.options.allowedLevels,
      allowedModes: initialTypeConf.options.allowedModes,
    },
  };
  return conf;
}

function getActivityTypeConf({ type, subType, miniactivityType }) {
  const activityTypeConf = ActivityTypesConf[type];
  const miniactivityTypeConf = activityTypeConf?.miniactivities?.find(
    (miniGame) => miniGame.miniactivityType == miniactivityType
  );
  const activitySubTypeConf = activityTypeConf[subType];
  const miniactivtySubTypeConf = activitySubTypeConf?.miniactivities?.find(
    (miniGame) => miniGame.miniactivityType == miniactivityType
  );
  return (
    miniactivtySubTypeConf ??
    activitySubTypeConf ??
    miniactivityTypeConf ??
    activityTypeConf
  );
}

function getActivityConfDefaults(activityTypeConf) {
  return {
    miniactivities: activityTypeConf.miniactivities?.map((miniactivity) => ({
      miniactivityType: miniactivity.miniactivityType,
      progress: miniactivity.progress,
      correctness: miniactivity.correctness,
      canLoadProgress: miniactivity.canLoadProgress,
      miniactivityTypeKey: miniactivity.miniactivityTypeKey,
      options: {
        level: miniactivity.options?.allowedLevels?.[0],
        repetitions: miniactivity.options?.repetitions && 1,
        mode: miniactivity.options?.allowedModes?.[0],
        questionsPerScreen: miniactivity.options?.questionsPerScreen,
      },
      default: miniactivity.default,
    })),
    themes: activityTypeConf.themes && [activityTypeConf.themes[0]],
    progress: activityTypeConf.progress,
    correctness: activityTypeConf.correctness,
    canLoadProgress: activityTypeConf.canLoadProgress,
    options: activityTypeConf.options && {
      level: activityTypeConf.options.allowedLevels?.[0],
      repetitions: activityTypeConf.options.repetitions && 1,
    },
  };
}

const ActivityTypesConf = {
  video: {
    progress: true,
    correctness: false,
    canLoadProgress: false,
    demoMode: (data) => data?.introVideoUrls?.length > 0,
    options: {
      repetitions: true,
    },
  },
  interactiveVideo: {
    progress: true,
    correctness: true,
    canLoadProgress: false,
    options: {
      repetitions: true,
    },
  },
  craft: {
    progress: true,
    correctness: false,
    canLoadProgress: false,
    label: 'craft',
    options: {
      repetitions: true,
    },
  },
  song: {
    progress: true,
    correctness: false,
    canLoadProgress: false,
    options: {
      repetitions: true,
    },
  },
  worksheet: {
    progress: false,
    correctness: false,
    canLoadProgress: false,
    options: {
      repetitions: false,
    },
  },
  pictureDictionary: {
    progress: false,
    correctness: false,
    canLoadProgress: false,
    options: {
      repetitions: false,
    },
  },
  shortStory: {
    getConf: (type, initialTypeConf) => ({
      miniactivities: initialTypeConf.miniactivities.map(
        (miniactivityInitialConf) => {
          return {
            label: typeLabel(type, miniactivityInitialConf.miniactivityType),
            miniactivityType: miniactivityInitialConf.miniactivityType,
            progress: miniactivityInitialConf.progress,
            correctness: miniactivityInitialConf.correctness,
            canLoadProgress: miniactivityInitialConf.canLoadProgress,
            options: miniactivityInitialConf.options,
          };
        }
      ),
    }),
    miniactivities: [
      {
        label: 'Story',
        miniactivityType: 'story',
        miniactivityTypeKey: 'slideIds',
        progress: true,
        correctness: false,
        canLoadProgress: false,
        options: {
          repetitions: true,
        },
      },
      {
        label: 'Sequencing',
        miniactivityType: 'sequencing',
        miniactivityTypeKey: 'dragItems',
        progress: true,
        correctness: false,
        canLoadProgress: false,
        options: {
          repetitions: true,
        },
      },
      {
        label: 'Quiz',
        miniactivityType: 'quiz',
        miniactivityTypeKey: 'questionIds',
        progress: true,
        correctness: true,
        canLoadProgress: true,
        options: {
          repetitions: true,
        },
      },
    ],
  },
  game: {
    classic: {
      getConf: (type, initialTypeConf, activityData) => {
        let miniactivities = initialTypeConf.miniactivities.map(
          (miniactivityInitialConf) => {
            const minigameConf = activityData.gameConf.menu.buttons.find(
              (button) =>
                miniactivityInitialConf.miniactivityTypeKey.some((typeKey) =>
                  button.id.toLowerCase().includes(typeKey.toLowerCase())
                )
            );
            return minigameConf
              ? {
                  label: typeLabel(
                    type,
                    activityData?.subType,
                    miniactivityInitialConf.miniactivityType
                  ),
                  miniactivityType: miniactivityInitialConf.miniactivityType,
                  progress: miniactivityInitialConf.progress,
                  correctness: miniactivityInitialConf.correctness,
                  canLoadProgress: miniactivityInitialConf.canLoadProgress,
                  options: {
                    repetitions: miniactivityInitialConf.options.repetitions,
                    allowedLevels: [
                      ...(minigameConf.buttons
                        ? minigameConf.buttons.map((button) =>
                            button.buttonText.toLowerCase()
                          )
                        : ['easy']),
                    ],
                    allowedModes: miniactivityInitialConf.options.allowedModes,
                  },
                }
              : null;
          }
        );
        return {
          miniactivities: miniactivities.filter((miniactivy) => miniactivy),
        };
      },
      miniactivities: [
        {
          label: 'Memory Game',
          miniactivityType: 'memoryGame',
          miniactivityTypeKey: ['CardMatch'],
          progress: true,
          canLoadProgress: false, // Igual que Drag & Match
          correctness: false,
          options: {
            repetitions: true,
            allowedLevels: ['easy', 'normal', 'difficult'], // Igual que Drag & Match
          },
        },
        {
          label: 'Drag & Match',
          miniactivityType: 'dragAndMatch',
          miniactivityTypeKey: ['DragAndMatch'],
          progress: true,
          correctness: true,
          canLoadProgress: false,
          // Dentro de gameConf.menu.buttons.minigameConf o gameConf.menu.buttons.buttons.minigameConf hay un array que contiene las cofiguraciones de cada minijuego
          // El "type" de cada configuración es el que hay que comparar con "miniactivityTypeKey"
          options: {
            repetitions: true,
            allowedLevels: ['easy', 'normal', 'difficult'] /*
            Estos son los nombres de las dificultades que pueden aparecer, no las que hay que ofrecer siempre:
            En el JSON de la actividad para cada minijuego, hay dos opciones:
              - Solo hay una dificultad (que llamaremos "easy" por defecto)
                 gameConf.menu.buttons[].minigameConf existe
              - Hay varias dificultades (pero no necesariamente todas):
                  minigameConf no está ahí, sino que hay otro array buttons dentro y el minigameConf está
                  dentro de cada elemento del array (un elemento por nivel de dificultad):
                    gameConf.menu.buttons[].buttons[].buttonText
                  En el cada elemento del buttons anidado tienes la propiedad "buttonText" para sacar el nivel de dificultad (Tendrás que hacerle un .toLowerCase() porque algunos tienen mayúsculas y otros no ¯\_(ツ)_/¯ )
            */,
          },
        },
        {
          label: 'Concept Race',
          miniactivityType: 'conceptRace',
          miniactivityTypeKey: ['WordRace', 'ConceptRace'], // Igual que Drag & Match,
          progress: true,
          correctness: true,
          canLoadProgress: false,
          options: {
            repetitions: true,
            allowedLevels: ['easy', 'normal', 'difficult'], // Igual que Drag & Match
            allowedModes: ['multipleChoice', 'writing'],
          },
        },
      ],
    },
    /* conceptRace: {
      progress: true,
      correctness: true,
      canLoadProgress: false,
      demoMode: false,
      options: {
        repetitions: true,
        allowedLevels: ['easy', 'normal', 'difficult'],
        allowedModes: ['multipleChoice', 'writing'],
      },
    }, */
    bubbles: {
      // Este tiene un minijuego por tema gameConf.conf.menu.screens.themes.options
      // Theme name: gameConf.conf.menu.screens.themes.options[].spanish,
      // Theme key: gameConf.conf.menu.screens.themes.options[].id
      getConf: (type, initialTypeConf, activityData) => {
        const conf = createNormalConf(null, initialTypeConf);
        conf.themes =
          activityData.gameConf.conf.menu.screens.themes.options.map(
            (theme) => ({
              themeId: theme.id,
              label: theme.text,
            })
          );
        return conf;
      },
      progress: true,
      correctness: false,
      canLoadProgress: false,
      demoMode: () => true,
      options: {
        repetitions: true,
        allowedLevels: ['easy', 'normal'],
      },
    },
    imagesQuiz: {
      progress: true,
      correctness: true,
      canLoadProgress: true,
      demoMode: () => true,
      options: {
        repetitions: true,
        allowedLevels: ['easy', 'normal', 'difficult'],
      },
    },
    quiz: {
      progress: true,
      correctness: false,
      canLoadProgress: true,
      demoMode: () => true,
      options: {
        repetitions: true,
        allowedLevels: ['easy', 'normal', 'difficult'],
      },
    },
    skate: {
      getConf: (_, initialTypeConf, activityData) => {
        const conf = createNormalConf(null, initialTypeConf);
        conf.themes = activityData.gameConf.minigames;
        return conf;
      },
      progress: true,
      correctness: false,
      canLoadProgress: false,
      demoMode: () => true,
      options: {
        repetitions: true,
      },
    },
    sortImages: {
      getConf: (_, initialTypeConf) => {
        return initialTypeConf;
      },
      miniactivities: [
        {
          label: 'Training',
          miniactivityType: 'training',
          miniactivityTypeKey: ['Training'],
          progress: true,
          canLoadProgress: false,
          correctness: false,
          options: {
            repetitions: true,
          },
        },
        {
          label: 'Listen & Match',
          miniactivityType: 'conceptQuest',
          miniactivityTypeKey: ['ConceptQuest'],
          progress: true,
          correctness: true,
          canLoadProgress: true,
          options: {
            repetitions: true,
            questionsPerScreen: 4,
          },
        },
      ],
    },
  },
  realTime: {
    sortImages: {
      progress: true,
      correctness: true,
      canLoadProgress: false,
      demoMode: () => true,
      options: {
        repetitions: false,
      },
    },
  },
  external: {
    progress: false,
    correctness: false,
    canLoadProgress: false,
    options: {
      repetitions: false,
    },
  },
  comic: {
    progress: true,
    correctness: false,
    canLoadProgress: true,
    options: {
      repetitions: true,
    },
  },
  storyWithQuestions: {
    getConf: (_, initialTypeConf, activityData) => ({
      miniactivities: initialTypeConf.miniactivities.filter(
        (miniactivityConf) =>
          activityData.miniactivityTypes?.includes(
            miniactivityConf.miniactivityType
          ) ||
          (!activityData.miniactivityTypes && miniactivityConf.default)
      ),
    }),
    miniactivities: [
      {
        label: 'Story',
        miniactivityType: 'story',
        progress: true,
        correctness: false,
        canLoadProgress: true,
        options: {
          repetitions: true,
        },
      },
      {
        label: 'Story with questions',
        miniactivityType: 'quiz',
        progress: true,
        correctness: true,
        canLoadProgress: true,
        options: {
          repetitions: true,
        },
        default: true,
      },
    ],
  },
};

export {
  getActivityTypeConf as default,
  createActivityTypeConf,
  getActivityConfDefaults,
};
